import {
    IPlayerStateHandler,
    LevelDetails,
    SubtitleTrack
} from './interfaces-and-types';
import PlaybackStateHandler, { PlaybackState } from './partials/playback';

export type PlayerState = {
    alwaysShowControlBar: boolean,
    isPlaying: boolean,
    hasPlayed: boolean,
    isVideoElementFullScreen: boolean,
    isNativeFullScreen: boolean,
    isControlBarVisible: boolean,
    isIdle: boolean,
    isSidebarExpanded: boolean,
    levelsAvailable?: LevelDetails[],
    currentLevel?: LevelDetails,
    videoLoaded: boolean,
    videoDurationSecs: number,
    videoElapsedSecs: number,
    videoHeight: number,
    videoSource?: string,
    volume: number,
    muted: boolean,
    subtitleTracks: SubtitleTrack[],
    subtitleTrackActive: SubtitleTrack | null,
    subtitlesEnabled: boolean,
    subtitles: string[],

    // partial
    playback: Partial<PlaybackState>
}

export default class PlayerStateHandler implements IPlayerStateHandler {
    private alwaysShowControlBar = false;

    private isPlaying = false;

    private hasPlayed = false;

    private isVideoElementFullScreen = false;

    private isNativeFullScreen = false;

    private isControlBarVisible = false;

    private isIdle = false;

    private isSidebarExpanded = false;

    private levelsAvailable: LevelDetails[] = [];

    private currentLevel?: LevelDetails;

    private videoLoaded = false;

    private videoDurationSecs = NaN;

    private videoElapsedSecs = 0;

    private videoHeight = 0;

    private videoSource?: string;

    private volume = 0.5;

    private muted = false;

    private subtitleTracks: SubtitleTrack[] = [];

    private subtitleTrackActive: SubtitleTrack | null = null;

    private subtitlesEnabled = false;

    private subtitles: string[] = [];

    // partial handlers

    private playbackHandler: PlaybackStateHandler;

    constructor() {
        // partial handlers
        this.playbackHandler = new PlaybackStateHandler();
    }

    public setState(partial: Partial<PlayerState>): Partial<PlayerState> {
        this.alwaysShowControlBar = partial.alwaysShowControlBar ?? this.alwaysShowControlBar;
        this.isPlaying = partial.isPlaying ?? this.isPlaying;
        this.hasPlayed = partial.hasPlayed ?? this.hasPlayed;
        this.isVideoElementFullScreen = partial.isVideoElementFullScreen ?? this.isVideoElementFullScreen;
        this.isNativeFullScreen = partial.isNativeFullScreen ?? this.isNativeFullScreen;
        this.isControlBarVisible = partial.isControlBarVisible ?? this.isControlBarVisible;
        this.isIdle = partial.isIdle ?? this.isIdle;
        this.isSidebarExpanded = partial.isSidebarExpanded ?? this.isSidebarExpanded;
        this.levelsAvailable = partial.levelsAvailable ?? this.levelsAvailable;
        this.currentLevel = partial.currentLevel ?? this.currentLevel;
        this.videoLoaded = partial.videoLoaded ?? this.videoLoaded;
        this.videoDurationSecs = partial.videoDurationSecs ?? this.videoDurationSecs;
        this.videoElapsedSecs = partial.videoElapsedSecs ?? this.videoElapsedSecs;
        this.videoHeight = partial.videoHeight ?? this.videoHeight;
        this.videoSource = partial.videoSource ?? this.videoSource;
        this.volume = partial.volume ?? this.volume;
        this.muted = partial.muted ?? this.muted;
        this.subtitleTracks = partial.subtitleTracks ?? this.subtitleTracks;
        this.subtitleTrackActive = partial.subtitleTrackActive ?? this.subtitleTrackActive;
        this.subtitlesEnabled = partial.subtitlesEnabled ?? this.subtitlesEnabled;
        this.subtitles = partial.subtitles ?? this.subtitles;

        // partial handlers
        const playback = this.playbackHandler.setState(partial.playback ?? {});

        return {
            ...partial,
            playback
        };
    }

    public get state(): PlayerState {
        return {
            alwaysShowControlBar: this.alwaysShowControlBar,
            isPlaying: this.isPlaying,
            hasPlayed: this.hasPlayed,
            isVideoElementFullScreen: this.isVideoElementFullScreen,
            isNativeFullScreen: this.isNativeFullScreen,
            isControlBarVisible: this.isControlBarVisible,
            isIdle: this.isIdle,
            isSidebarExpanded: this.isSidebarExpanded,
            levelsAvailable: this.levelsAvailable,
            currentLevel: this.currentLevel,
            videoLoaded: this.videoLoaded,
            videoDurationSecs: this.videoDurationSecs,
            videoElapsedSecs: this.videoElapsedSecs,
            videoHeight: this.videoHeight,
            videoSource: this.videoSource,
            volume: this.volume,
            muted: this.muted,
            subtitleTracks: this.subtitleTracks,
            subtitleTrackActive: this.subtitleTrackActive,
            subtitlesEnabled: this.subtitlesEnabled,
            subtitles: this.subtitles,

            // partials
            playback: this.playbackHandler.state
        };
    }
}
